import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/hover-card';
import { HoverCardArrow } from '@radix-ui/react-hover-card';
import { cn } from '@/lib/utils';
import { getChatUserDisplayName } from '@/lib/data-transformers/comment';
import { memo, useMemo } from 'react';
import type { UserDetails } from '@/types/user';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

/**
 * Reusable component for displaying user information in a hover card.
 *
 * @param {UserHoverCardProps} props - The properties for the UserHoverCard component.
 * @returns {JSX.Element} The rendered UserHoverCard component.
 */
function UserHoverCard({
  user,
  children,
  triggerClassName = 'font-semibold text-blue-800 cursor-pointer hover:text-blue-600 transition-colors',
  side = 'top',
  uniqueKey,
  arrowClassName,
  contentClassName,
}: UserHoverCardProps) {
  // Create properly capitalized full name
  const fullName = useMemo(() => getChatUserDisplayName(user), [user]);

  if (user?.id == null) {
    return <span className={cn(triggerClassName)}>{children}</span>;
  }

  return (
    <HoverCard key={uniqueKey}>
      <HoverCardTrigger asChild>
        <span className={cn(triggerClassName)}>{children}</span>
      </HoverCardTrigger>
      <HoverCardContent side={side} className={cn('w-80 p-0', contentClassName)}>
        <HoverCardArrow className={cn('fill-popover', arrowClassName)} />
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar with dark colors for white text */}
            <UserHoverCard.Avatar fullName={fullName} userId={user.id} />

            {/* User Info */}
            <div className="min-w-0 flex-1">
              <UserHoverCard.Info
                email={user.email}
                mobileNo={user.mobileNo}
                fullName={fullName}
                username={user.username}
              />
            </div>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

UserHoverCard.displayName = 'UserHoverCard';

/**
 * Avatar component for displaying user avatar with consistent color.
 *
 * @param {Object} props - The properties for the Avatar component.
 * @param {string} props.fullName - The full name of the user.
 * @param {number} props.userId - The unique ID of the user.
 * @returns {JSX.Element} The rendered Avatar component.
 */
UserHoverCard.Avatar = memo(({ fullName, userId }: { fullName: string; userId: number }) => {
  // Get consistent avatar color based on user
  const avatarColor = useMemo(() => getAvatarColor(userId, fullName), [userId, fullName]);
  return (
    <div className="flex-shrink-0">
      <Avatar className="h-10 w-10">
        <AvatarFallback
          className={cn(
            'bg-gradient-to-br text-sm font-semibold text-white shadow-sm',
            avatarColor
          )}
        >
          {fullName?.charAt(0)?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>
    </div>
  );
});
UserHoverCard.Avatar.displayName = 'AvatarInUserHoverCard';

/**
 * Info component for displaying user information such as email and mobile number.
 *
 * @param {Object} props - The properties for the Info component.
 * @param {string} props.email - The email of the user.
 * @param {string} props.mobileNo - The mobile number of the user.
 * @param {string} props.fullName - The full name of the user.
 * @param {string} props.username - The username of the user.
 * @returns {JSX.Element} The rendered Info component.
 */
UserHoverCard.Info = memo(
  ({
    email,
    mobileNo,
    fullName,
    username,
  }: {
    email: string;
    mobileNo: string;
    fullName: string;
    username: string;
  }) => {
    return (
      <>
        <h4 className="text-foreground truncate text-sm font-semibold">{fullName}</h4>
        <p className="text-muted-foreground mt-0.5 text-xs font-medium italic">@{username}</p>

        {/* Contact Info */}
        <div className="mt-3 space-y-1.5">
          {email && (
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <svg className="h-3 w-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <span className="truncate">{email}</span>
            </div>
          )}

          {mobileNo && (
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <svg className="h-3 w-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className="truncate">{mobileNo}</span>
            </div>
          )}
        </div>
      </>
    );
  }
);
UserHoverCard.Info.displayName = 'UserInfoInUserHoverCard';

export { UserHoverCard };

/**
 * Generates a consistent color based on user ID or name.
 * Optimized for white text with good contrast.
 *
 * @param {number} userId - The unique ID of the user.
 * @param {string} name - The name of the user.
 * @returns {string} The generated color class for the avatar.
 */
function getAvatarColor(userId: number, name: string): string {
  // Darker color combinations that work well with white text
  const colors = [
    'from-slate-600 to-slate-700',
    'from-gray-600 to-gray-700',
    'from-zinc-600 to-zinc-700',
    'from-neutral-600 to-neutral-700',
    'from-stone-600 to-stone-700',
    'from-red-600 to-red-700',
    'from-orange-600 to-orange-700',
    'from-amber-600 to-amber-700',
    'from-yellow-600 to-yellow-700',
    'from-lime-600 to-lime-700',
    'from-green-600 to-green-700',
    'from-emerald-600 to-emerald-700',
    'from-teal-600 to-teal-700',
    'from-cyan-600 to-cyan-700',
    'from-sky-600 to-sky-700',
    'from-blue-600 to-blue-700',
    'from-indigo-600 to-indigo-700',
    'from-violet-600 to-violet-700',
    'from-purple-600 to-purple-700',
    'from-fuchsia-600 to-fuchsia-700',
    'from-pink-600 to-pink-700',
    'from-rose-600 to-rose-700',
  ];

  // Use user ID to get consistent color for the same user
  const colorIndex = (userId + name.length) % colors.length;
  return colors[colorIndex];
}

/**
 * Type definition for UserHoverCard component props.
 */
type UserHoverCardProps = {
  /**
   * The user details to display.
   */
  user: UserDetails;
  /**
   * The content to trigger the hover card.
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the trigger.
   */
  triggerClassName?: string;
  /**
   * Side where the hover card should appear.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Unique key for the component.
   */
  uniqueKey?: string;
  /**
   * Additional CSS classes for the arrow.
   */
  arrowClassName?: string;
  /**
   * Additional CSS classes for the content.
   */
  contentClassName?: string;
}
